import { useEffect } from 'react';
import { cva } from 'class-variance-authority';
import { motion } from 'motion/react';
import { LogoAnimated } from '@nula/core-ui';

import { markSplashShown } from '@/stores/use-boot-store';
import styles from '../app.module.css';

const splash = cva(styles.splash);
const splashLogoEl = cva(styles.splashLogo);

export interface AppSplashProps {
  /**
   * When true (default), a minimum-display timer calls markSplashShown() to
   * advance the boot state machine. Set false to render the SAME splash visual
   * WITHOUT touching boot state — e.g. the /auth/callback landing page reuses
   * AppSplash so its loading screen is identical to the app's (no flash on
   * hand-off), but it must not drive the boot machine. */
  advanceBootState?: boolean;
  /**
   * Animation speed multiplier (default 1). Values > 1 SLOW the animation by
   * that factor. The auth-callback page passes 10 so the logo draws on gently
   * and never finishes-then-sits while waiting an unknown amount of time for the
   * token exchange + boot gate — a fast one-shot that freezes looks abrupt when
   * the gate then takes over. When slowed, the logo loops so it stays alive.
   */
  speed?: number;
}

/**
 * Logo-on-load splash. Renders once per fresh page load (the underlying
 * splash-shown flag in @/stores/use-boot-store is module-local and survives React
 * remounts but resets on reload). When the minimum-display timer fires,
 * we call markSplashShown() to advance the boot state machine.
 *
 * Reused verbatim by the auth-callback page (advanceBootState={false}) so the
 * post-OAuth loading screen is visually identical to the app splash.
 */
export function AppSplash({ advanceBootState = true, speed = 1 }: AppSplashProps = {}) {
  // Scale every animation by `speed` (>1 = slower). When slowed, loop the logo
  // so it stays animating instead of completing a long one-shot then freezing.
  const slowed = speed > 1;
  const orbDuration = 3 * speed;
  const logoDuration = 2000 * speed;
  const subtitleDuration = 1.2 * speed;
  const subtitleDelay = 0.6 * speed;
  // Minimum animation duration — gives the logo draw-on enough time to
  // complete before the boot state machine advances past 'splash'. The
  // useBootState hook also gates on isLoading, so if auth resume is still
  // in flight when this timer fires, we stay on splash until both
  // conditions clear.
  useEffect(() => {
    if (!advanceBootState) return;
    const t = setTimeout(() => markSplashShown(), 2500);
    return () => clearTimeout(t);
  }, [advanceBootState]);

  return (
    <motion.div
      key="app-splash"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.2, ease: 'easeInOut' }}
      className={splash()}
    >
      {/* Ambient orbs — decorative; sizes are design-specific (no token match),
          passed via CSS custom properties */}
      <motion.div
        animate={{ opacity: [0, 0.5, 0] }}
        transition={{ duration: orbDuration, ease: 'easeInOut', repeat: slowed ? Infinity : 0 }}
        className={styles.splashOrb}
        style={{ '--orb-size': '500px' } as React.CSSProperties}
      />
      <motion.div
        animate={{ opacity: [0, 0.4, 0] }}
        transition={{ duration: orbDuration, ease: 'easeInOut', delay: 0.2 * speed, repeat: slowed ? Infinity : 0 }}
        className={styles.splashOrb2}
        style={{ '--orb-size': '600px' } as React.CSSProperties}
      />

      {/* Logo */}
      <div className={splashLogoEl()}>
        <LogoAnimated size={240} loop={slowed} duration={logoDuration} holdDuration={400 * speed} />

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: subtitleDuration, delay: subtitleDelay, ease: 'easeOut' }}
          className={styles.splashTextRow}
        >
          <div className={styles.splashDivider} />
          <p className={styles.splashSubtitle}>
            Your space to think
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
}
